/**
 * Table component - Configurable table for PDF generation
 *
 * Creates formatted tables with headers, alternating rows, and footer support.
 */

import type { Content, ContentTable, TableCell, TableLayout } from 'pdfmake/interfaces';
import type { PdfTableOptions, PdfTableColumn } from '../types';
import { PDF_COLORS } from '../types';
import { parseHtmlContent } from '../utils/parseQuillHtml';

/**
 * Format a value based on column configuration
 */
function formatValue(
    value: unknown,
    column: PdfTableColumn
): string {
    if (value == null) return '';

    if (column.isCurrency) {
        const num = typeof value === 'number' ? value : parseFloat(String(value));
        if (isNaN(num)) return '';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(num);
    }

    if (column.isDate) {
        const date = value instanceof Date ? value : new Date(String(value));
        if (isNaN(date.getTime())) return String(value);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    }

    return String(value);
}

/**
 * Get value from a row using column definition
 */
function getValue(row: Record<string, unknown>, column: PdfTableColumn): unknown {
    if (typeof column.field === 'function') {
        return column.field(row);
    }
    return row[column.field];
}

/**
 * Create a data table for PDF
 *
 * @param options - Table configuration options
 * @returns pdfmake Content for the table
 */
export function createTable(options: PdfTableOptions): Content {
    const {
        columns,
        data,
        showHeader = true,
        alternatingRows = false, // Default to no alternating rows (matches original)
        alternatingColor = '#f9f9f9',
        showTotals = false,
        footer,
        headerBackground, // No default - undefined means no background
        headerColor = PDF_COLORS.textBlack,
        borderColor = PDF_COLORS.borderGray,
    } = options;

    const body: TableCell[][] = [];

    // Header row
    if (showHeader) {
        const headerRow: TableCell[] = columns.map(col => {
            const cell: TableCell = {
                text: col.header,
                color: headerColor,
                alignment: col.alignment || 'left',
                bold: true,
                fontSize: 9,
            };
            // Only apply background if specified
            if (headerBackground) {
                cell.fillColor = headerBackground;
            }
            return cell;
        });
        body.push(headerRow);
    }

    // Data rows
    data.forEach((row, rowIndex) => {
        const dataRow: TableCell[] = columns.map(col => {
            const value = getValue(row, col);
            const alignment = col.alignment || (col.isCurrency ? 'right' : 'left');

            // Rich HTML content: parse into a stack of pdfmake Content nodes
            if (col.isHtml && typeof value === 'string' && value) {
                const richContent = parseHtmlContent(value);
                if (richContent.length > 0) {
                    const cell = {
                        stack: richContent,
                        alignment,
                    } as unknown as TableCell;
                    if (alternatingRows && rowIndex % 2 === 1) {
                        (cell as Record<string, unknown>).fillColor = alternatingColor;
                    }
                    if (col.style) {
                        (cell as Record<string, unknown>).style = col.style;
                    }
                    return cell;
                }
            }

            const formattedValue = formatValue(value, col);

            const cell: TableCell = {
                text: formattedValue,
                alignment,
            };

            // Prevent text wrapping in this column
            if (col.noWrap) {
                cell.noWrap = true;
            }

            // Apply alternating row color
            if (alternatingRows && rowIndex % 2 === 1) {
                cell.fillColor = alternatingColor;
            }

            // Apply custom style if specified
            if (col.style) {
                cell.style = col.style;
            }

            return cell;
        });
        body.push(dataRow);
    });

    // Footer/totals row
    if (showTotals && footer) {
        const footerRow: TableCell[] = columns.map(col => {
            const value = getValue(footer, col);
            const formattedValue = formatValue(value, col);

            return {
                text: formattedValue,
                alignment: col.alignment || (col.isCurrency ? 'right' : 'left'),
                bold: true,
            };
        });
        body.push(footerRow);
    }

    // Calculate widths
    const widths = columns.map(col => col.width || '*');

    // Custom table layout for borders (clean lines, no vertical borders)
    const hasFooter = showTotals && footer;
    const tableLayout: TableLayout = {
        hLineWidth: (i: number, node: ContentTable) => {
            // Line after header
            if (i === 1) return 0.5;
            // Line before footer (if exists)
            if (hasFooter && i === node.table.body.length - 1) return 0.5;
            // Line after footer
            if (hasFooter && i === node.table.body.length) return 0.5;
            return 0;
        },
        vLineWidth: () => 0,
        hLineColor: () => borderColor,
        paddingLeft: () => 4,
        paddingRight: () => 4,
        paddingTop: () => 3,
        paddingBottom: () => 3,
    };

    const table: ContentTable = {
        table: {
            headerRows: showHeader ? 1 : 0,
            keepWithHeaderRows: Math.min(3, data.length),
            dontBreakRows: true,
            widths,
            body,
        },
        layout: tableLayout,
    };

    return table;
}

/**
 * Create a simple key-value table (2 columns)
 */
export function createKeyValueTable(
    items: Array<{ key: string; value: string | number; isCurrency?: boolean }>,
    options: { keyWidth?: number | '*'; valueWidth?: number | '*'; keyBold?: boolean } = {}
): Content {
    const { keyWidth = '*', valueWidth = 'auto', keyBold = false } = options;

    const body: TableCell[][] = items.map(item => {
        let valueText: string;
        if (item.isCurrency && typeof item.value === 'number') {
            valueText = new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
            }).format(item.value);
        } else {
            valueText = String(item.value);
        }

        return [
            { text: item.key, bold: keyBold },
            { text: valueText, alignment: 'right' as const },
        ];
    });

    return {
        table: {
            widths: [keyWidth, valueWidth],
            body,
        },
        layout: 'noBorders',
    };
}

export default createTable;
